import { computed, inject } from '@angular/core';
import { signalStore, withComputed } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { getAccountSubscription } from '@reducers/account/account-service.selectors';
import { selectAuthUserId } from '@reducers/auth/auth.selectors';
import { AppState } from '@reducers/interfaces';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';

import { PendingRequestsApiService } from '../requests/api/pending-requests-api.service';
import type { PendingRow } from '../requests/api/requests.models';
import { exhaustedSource, freshSource, withPagedSources, type SourceState } from '../requests/request-paging';
import { isAbsenceRow, isExchangeRow, isOpenShiftRow } from '../requests/request-rows.helper';

type SourceKey = 'incomingExchange' | 'outgoingExchange' | 'openShift' | 'ownAbsence';

const SOURCE_KEYS: SourceKey[] = ['incomingExchange', 'outgoingExchange', 'openShift', 'ownAbsence'];

const canUseOpenShifts = (legacyStore: Store<AppState>): boolean => {
  const subscription = legacyStore.selectSignal(getAccountSubscription)();

  return !!subscription && hasAtLeastSubscriptionPlan(PlanType.BASIC, subscription);
};

const identifyRow = (row: PendingRow) => (isOpenShiftRow(row) ? row.occurrence_id : row.id);

export const MyRequestsPanelStore = signalStore(
  withPagedSources<SourceKey, PendingRow>({
    keys: SOURCE_KEYS,
    buildState: (): Record<SourceKey, SourceState<PendingRow>> => {
      const openShiftsAvailable = canUseOpenShifts(inject<Store<AppState>>(Store));

      return {
        incomingExchange: freshSource(),
        outgoingExchange: freshSource(),
        openShift: openShiftsAvailable ? freshSource() : exhaustedSource(),
        ownAbsence: freshSource(),
      };
    },
    buildResources: () => {
      const api = inject(PendingRequestsApiService);
      const features = inject(FeatureService);
      const legacyStore = inject<Store<AppState>>(Store);
      const authUserId = legacyStore.selectSignal(selectAuthUserId);

      return {
        paginationEnabled: features.isEnabled(featureFlag.TMP_PAGINATED_REQUESTS_PANEL),
        canLoad: () => !!authUserId(),
        fetchers: {
          incomingExchange: (page) => api.listIncomingExchanges(authUserId() ?? '', page),
          outgoingExchange: (page) => api.listOutgoingExchanges(authUserId() ?? '', page),
          openShift: (page) => api.listOpenShiftsAwaitingReaction(page),
          ownAbsence: (page) => api.listOwnPendingAbsences(authUserId() ?? '', page),
        },
      };
    },
    identifyRow,
  }),
  withComputed(({ sources }) => ({
    incomingExchangeRows: computed(() => sources()['incomingExchange'].rows.filter(isExchangeRow)),
    outgoingExchangeRows: computed(() => sources()['outgoingExchange'].rows.filter(isExchangeRow)),
    openShiftRows: computed(() => sources()['openShift'].rows.filter(isOpenShiftRow)),
    ownAbsenceRows: computed(() => sources()['ownAbsence'].rows.filter(isAbsenceRow)),
  })),
);
